









































































































/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import { mapState } from 'vuex';
import { HubButton, HubCard, HubIcon } from '@/components/HubComponents';
import {
  gmapsMap as GMaps,
  gmapsPopup as GPopup,
} from 'x5-gmaps';
import statisticsServices from '@/services/hub/statistics';
import VueApexCharts from 'vue-apexcharts';
import { ApexOptions } from 'apexcharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { jsPDF as JsPdf } from 'jspdf';

const defaultOptionsDonutCharts = {
  chart: { type: 'donut' },
  legend: { fontSize: '20px', offsetY: 80 },
  plotOptions: {
    pie: {
      customScale: 1.5,
      offsetY: 50,
      donut: {
        labels: {
          show: true,
          name: {
            formatter(value) {
              return value.split(':')[0];
            },
          },
        },
      },
    },
  },
} as ApexOptions;

export default Vue.extend({
  name: 'RegionStatistics',

  components: {
    HubButton,
    HubCard,
    HubIcon,
    GMaps,
    GPopup,
    VueApexCharts,
  },

  props: {
    finalDate: {
      type: String,
      required: true,
    },
    initialDate: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      allData: {} as Components.RegionStatistics.AllData,
      browserOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('browsers') },
      } as ApexOptions,
      browserSeries: [] as number[],
      currentType: 'global' as Services.Statistics.Type,
      isLoading: false,
      justClickedPreviousLevelButton: false,
      mapMarkers: [] as Components.RegionStatistics.MapMarker[],
      mapOptions: {
        center: { lat: 10, lng: 10 },
        mapTypeControl: false,
        streetViewControl: false,
        zoom: 2,
      } as google.maps.MapOptions,
      nextOrPreviousType: {
        city: {
          previous: 'state',
        },
        country: {
          next: 'state',
          previous: 'global',
        },
        global: {
          next: 'country',
        },
        state: {
          next: 'city',
          previous: 'country',
        },
      } as Components.RegionStatistics.NextOrPreviousType,
      operationalSystemOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('operational_systems') },
      } as ApexOptions,
      operationalSystemSeries: [] as number[],
      platformOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('platforms') },
      } as ApexOptions,
      platformSeries: [] as number[],
      totalOnline: 0,
      unknownLocation: 0,
    };
  },

  computed: {
    ...mapState(['events', 'user']),
    beginDate(): Date {
      return new Date(`${this.initialDate} 00:00`);
    },
    endDate(): Date {
      // 86400000: one day in milliseconds ==> 60 sec * 60 min * 24h * 1000 ms
      return new Date(
        new Date(`${this.finalDate} 00:00`).getTime() + 86400000,
      );
    },
    eventState(): Store.Events.Event {
      return this.events.event;
    },
    userState(): Store.User.State {
      return this.user;
    },
  },

  watch: {
    currentType(): void {
      if (this.justClickedPreviousLevelButton && this.currentType !== 'city') {
        this.setFullStatistics(this.allData[this.currentType]);

        this.mapOptions = {
          ...this.mapOptions,
          zoom: (this.mapOptions.zoom as number) - 1,
        };
      }

      this.justClickedPreviousLevelButton = false;
    },
    async finalDate(): Promise<void> {
      this.isLoading = true;
      this.currentType = 'global';

      const response = await this.getFullStatistics({
        beginDate: this.beginDate,
        customerPlansId: this.eventState.customer_plans_id,
        endDate: this.endDate,
        eventHash: this.eventState.event_hash,
        type: this.currentType,
      });

      this.mapOptions = {
        center: { lat: 10, lng: 10 },
        zoom: 2,
      };

      this.allData = {
        ...this.allData,
        global: response,
      };

      this.setFullStatistics(response);
      this.isLoading = false;
    },
    async initialDate(): Promise<void> {
      this.isLoading = true;
      this.currentType = 'global';

      const response = await this.getFullStatistics({
        beginDate: this.beginDate,
        customerPlansId: this.eventState.customer_plans_id,
        endDate: this.endDate,
        eventHash: this.eventState.event_hash,
        type: this.currentType,
      });

      this.mapOptions = {
        center: { lat: 10, lng: 10 },
        zoom: 2,
      };

      this.allData = {
        ...this.allData,
        global: response,
      };

      this.setFullStatistics(response);
      this.isLoading = false;
    },
  },

  created() {
    // Adding the listener for the "onClickOnDataDownload__region" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$on('onClickOnDataDownload__region', this.onClickOnDataDownload);
  },

  beforeDestroy() {
    // Removing the listener for the "onClickOnDataDownload__region" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$off('onClickOnDataDownload__region');
  },

  async mounted() {
    this.isLoading = true;

    const response = await this.getFullStatistics({
      beginDate: this.beginDate,
      customerPlansId: this.eventState.customer_plans_id,
      endDate: this.endDate,
      eventHash: this.eventState.event_hash,
      type: this.currentType,
    });

    this.allData = {
      ...this.allData,
      global: response,
    };

    this.setFullStatistics(response);
    this.isLoading = false;
  },

  methods: {
    firstCapitalLetter(value: string): string {
      return `${value.slice(0, 1).toUpperCase()}${value.slice(1).toLowerCase()}`;
    },
    formatValue(value: number): string {
      return new Intl.NumberFormat().format(value);
    },
    async getFullStatistics(
      payload: Services.Statistics.GetRegion,
    ): Promise<Components.RegionStatistics.GetFullStatistics> {
      const region = await this.getRegion(payload);

      const platform = await this.getRegion({ ...payload, filter: 'platform' });

      const os = await this.getRegion({ ...payload, filter: 'os' });

      const browser = await this.getRegion({ ...payload, filter: 'browser' });

      return {
        browser,
        os,
        platform,
        region,
      };
    },
    async getRegion(
      payload: Services.Statistics.GetRegion,
    ): Promise<Services.Statistics.Response.RegionView[]> {
      return statisticsServices.getRegion(payload)
        .then(({ data }) => data.views)
        .catch((error) => {
          console.error('RegionStatistics - method: "getRegion()" ', error);

          this.showNotification('error_fetching_statistical_data', 'error');

          throw new Error(error);
        });
    },
    async onClickMapMarker(marker: Components.RegionStatistics.MapMarker): Promise<void> {
      this.isLoading = true;

      if (this.currentType === 'city') {
        this.isLoading = false;
        return;
      }

      this.currentType = this.nextOrPreviousType[this.currentType].next;

      let payload = {};

      switch (this.currentType) {
        case 'city':
          if (this.allData.state.region.length <= 1) {
            this.isLoading = false;
            this.currentType = this.nextOrPreviousType.city.previous;
            return;
          }

          this.mapOptions = {
            center: marker.position,
            zoom: 7,
          };

          payload = {
            city: marker.label,
            country: this.allData.country.selectedCountry,
            state: this.allData.state.selectedState,
          };
          break;
        case 'country':
          this.mapOptions = {
            center: marker.position,
            zoom: 4,
          };

          this.allData.country = {
            ...this.allData.country,
            selectedCountry: marker.label,
          };

          payload = { country: marker.label };
          break;
        case 'state':
          this.mapOptions = {
            center: marker.position,
            zoom: 6,
          };

          this.allData.state = {
            ...this.allData.state,
            selectedState: marker.label,
          };

          payload = {
            country: this.allData.country.selectedCountry,
            state: marker.label,
          };
          break;
        default: break;
      }

      const response = await this.getFullStatistics({
        beginDate: this.beginDate,
        customerPlansId: this.eventState.customer_plans_id,
        endDate: this.endDate,
        eventHash: this.eventState.event_hash,
        type: this.currentType,
        ...payload,
      });

      if (this.currentType !== 'city') {
        this.allData[this.currentType] = {
          ...this.allData[this.currentType],
          ...response,
        };
      }

      this.setFullStatistics(response);
      this.isLoading = false;
    },
    async onClickOnDataDownload(
      exportFormat: Views.ViewStatistics.ExportFormat,
    ): Promise<void> {
      const date = this.userState.settings.lang === 'en-US'
        ? `${this.initialDate.replace(/-/g, '/')} ${
          this.$t('until')} ${
          this.finalDate.replace(/-/g, '/')}`
        : `${this.initialDate.split('-').reverse().join('/')} ${
          this.$t('until')} ${
          this.finalDate.split('-').reverse().join('/')}`;

      const filename = `${
        this.$t('region_statistics').toString().replace(/\s/g, '_')}-${
        date
          .replace(/\//g, '-')
          .replace(' - ', '-')
          .replace(` ${this.$t('until')} `, '--')
      }`.toLowerCase();

      const canvasElement = await html2canvas(
        this.$el.querySelector('#region-statistics__print') as HTMLElement,
        {
          useCORS: true,
          allowTaint: false,
          scale: 2,
          ignoreElements: (node) => (
            node.classList.value.includes('previous-level-button')
            || node.classList.value.includes('gm-control-active')
            || node.classList.value.includes('gmnoprint')
          ),
        },
      ).catch((error) => console.error('Error converting HTML to canvas element', error));

      if (!(canvasElement instanceof HTMLCanvasElement)) return;

      switch (exportFormat) {
        case 'png': {
          const url = canvasElement.toDataURL('image/png');

          saveAs(url, `${filename}.png`);
          window.URL.revokeObjectURL(url);
          break;
        }
        case 'pdf': {
          const pdf = new JsPdf();
          const height = pdf.internal.pageSize.getHeight() * 0.98;
          const width = pdf.internal.pageSize.getWidth() * 0.9;

          pdf.addImage(canvasElement, 10, 5, width, height);
          pdf.save(filename);
          break;
        }
        default: break;
      }

      this.$emit('exported');
    },
    setFullStatistics(data: Components.RegionStatistics.GetFullStatistics): void {
      this.setRegion(data.region);
      this.setPlatform(data.platform);
      this.setOperationalSystems(data.os);
      this.setBrowser(data.browser);
    },
    setBrowser(data: Services.Statistics.Response.RegionView[]): void {
      this.browserOptions = {
        ...this.browserOptions,
        labels: data.map((browser) => (browser._id.browser as string).concat(`: ${browser.count}`)),
      };

      this.browserSeries = data.map((browser) => browser.count);
    },
    setOperationalSystems(data: Services.Statistics.Response.RegionView[]): void {
      this.operationalSystemOptions = {
        ...this.operationalSystemOptions,
        labels: data.map((os) => (os._id.os as string).concat(`: ${os.count}`)),
      };

      this.operationalSystemSeries = data.map((os) => os.count);
    },
    setPlatform(data: Services.Statistics.Response.RegionView[]): void {
      this.platformOptions = {
        ...this.platformOptions,
        labels: data.map(
          (platform) => this.firstCapitalLetter(platform._id.platform as string)
            .concat(`: ${platform.count}`),
        ),
      };

      this.platformSeries = data.map((platform) => platform.count);
    },
    setRegion(data: Services.Statistics.Response.RegionView[]): void {
      this.mapMarkers = [];
      this.totalOnline = 0;
      this.unknownLocation = 0;

      data.forEach((region) => {
        const label = (region._id.country || region._id.state || region._id.city) as string;

        this.totalOnline += region.count;

        if (label.toLowerCase() === 'unknown') {
          this.unknownLocation += region.count;
          return;
        }

        this.mapMarkers.push({
          label,
          position: {
            lat: parseFloat(`${region.latitude}`),
            lng: parseFloat(`${region.longitude}`),
          },
          value: region.count,
        });
      });
    },
    showNotification(text: string, type: 'error' | 'success'): void {
      this.$store.dispatch('notification/create', { text, type });
    },
  },
});
